import { useEffect, useRef, useState } from 'react'
import { useAssistantService } from '../services/assistantService'

interface ChatMessage {
  role: 'user' | 'assistant'
  text: string
  typing?: boolean
}

// Reveal an assistant reply over roughly this many ticks, regardless of its
// length, so short and long replies both animate in at a natural pace
// instead of the fixed per-character delay making long replies crawl.
const TYPING_TICKS = 90
const TYPING_TICK_MS = 20

// Just the chip labels shown in the UI — the backend (POST /assistant/chat)
// owns the actual keyword matching and canned answers.
const EXAMPLE_QUESTIONS: string[] = [
  'Which clients need review?',
  "What's the status of my active matters?",
  'What is a Section 27?',
  'Draft a message chasing a client for their bank details',
  'A mortgagee has gone out of business and still holds a discharge — what now?',
]

const SparkleIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z" />
  </svg>
)

// Docked as a permanent right-hand column on large screens, like an agent
// sidebar. Below the `lg` breakpoint there's no room for three columns, so
// it collapses into a toggleable slide-over instead (floating button +
// backdrop), controlled by the same `open` state.
export const AiAssistant: React.FC = () => {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: 'assistant', text: "Hi! I'm your AI assistant. Ask me anything or try one of the frequently asked questions below." },
  ])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const { sendChatMessage } = useAssistantService()
  const typingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    return () => {
      if (typingTimerRef.current) clearInterval(typingTimerRef.current)
    }
  }, [])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: 'end' })
  }, [messages])

  // Types the reply into the chat one chunk at a time so it reads like a
  // live agent responding, rather than the full answer popping in at once.
  const typeOutMessage = (fullText: string) => {
    setMessages((prev) => [...prev, { role: 'assistant', text: '', typing: true }])
    const step = Math.max(1, Math.ceil(fullText.length / TYPING_TICKS))
    let shown = 0
    typingTimerRef.current = setInterval(() => {
      shown = Math.min(fullText.length, shown + step)
      const done = shown >= fullText.length
      setMessages((prev) => {
        const next = [...prev]
        next[next.length - 1] = { role: 'assistant', text: fullText.slice(0, shown), typing: !done }
        return next
      })
      if (done) {
        if (typingTimerRef.current) clearInterval(typingTimerRef.current)
        typingTimerRef.current = null
        setSending(false)
      }
    }, TYPING_TICK_MS)
  }

  const send = async (text: string) => {
    if (!text.trim() || sending) return
    setMessages((prev) => [...prev, { role: 'user', text }])
    setInput('')
    setSending(true)
    try {
      const reply = await sendChatMessage(text)
      typeOutMessage(reply)
    } catch (err) {
      typeOutMessage(err instanceof Error ? err.message : "Sorry, I couldn't reach the assistant service.")
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    send(input)
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open assistant"
        title="Open assistant"
        className="lg:hidden fixed bottom-6 right-6 z-40 w-14 h-14 rounded-lg bg-primary text-white shadow-lg flex items-center justify-center hover:opacity-90"
      >
        <SparkleIcon className="w-6 h-6" />
      </button>

      {open && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/20"
          onClick={() => setOpen(false)}
        />
      )}

      <div
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-sm min-h-0 bg-white border-l border-gray-200 shadow-2xl flex flex-col transform transition-transform duration-200 ease-in-out
          lg:static lg:z-auto lg:w-96 lg:max-w-none lg:shrink-0 lg:translate-x-0 lg:shadow-none lg:transition-none
          ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div
          className="px-4 py-4 flex items-center justify-between shrink-0"
          style={{ background: 'linear-gradient(135deg, #0F2742, #1F3F66)' }}
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-accent">
              <SparkleIcon className="w-4.5 h-4.5" />
            </div>
            <div>
              <h3 className="font-semibold text-white text-sm leading-tight">Conveyancing Assistant</h3>
              <p className="text-xs text-white/50 leading-tight">Always online</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close assistant"
            title="Close assistant"
            className="lg:hidden text-white/60 hover:text-white p-1"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-background">
          {messages.map((m, i) => (
            <div key={i} className={`flex items-end gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {m.role === 'assistant' && (
                <div className="w-6 h-6 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <SparkleIcon className="w-3.5 h-3.5" />
                </div>
              )}
              <div
                className={`max-w-[80%] px-3 py-2 rounded-lg text-sm whitespace-pre-line shadow-sm ${
                  m.role === 'user'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-900 border border-gray-200'
                }`}
              >
                {m.text}
                {m.typing && <span className="inline-block w-1.5 h-3.5 -mb-0.5 ml-0.5 bg-primary/60 animate-pulse" />}
              </div>
            </div>
          ))}
          {sending && !messages[messages.length - 1]?.typing && (
            <div className="flex items-end gap-2 justify-start">
              <div className="w-6 h-6 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <SparkleIcon className="w-3.5 h-3.5" />
              </div>
              <div className="px-3 py-2 rounded-lg text-sm bg-white text-gray-500 border border-gray-200 shadow-sm flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.3s]" />
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.15s]" />
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="px-4 py-3 border-t border-gray-200 shrink-0 bg-white">
          <p className="text-xs text-gray-500 mb-2">Try asking:</p>
          <div className="flex flex-wrap gap-1 mb-3">
            {EXAMPLE_QUESTIONS.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => send(q)}
                disabled={sending}
                className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 disabled:opacity-50"
              >
                {q}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question..."
              disabled={sending}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={sending}
              aria-label="Send"
              title="Send"
              className="w-9 h-9 shrink-0 rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-50 flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.126A59.77 59.77 0 0 1 21.485 12 59.77 59.77 0 0 1 3.27 20.874L5.999 12Zm0 0h7.5" />
              </svg>
            </button>
          </form>
        </div>
      </div>
    </>
  )
}
